/**
 * StaffCalendarTab — vista giornaliera dei KPI del dipendente.
 *
 * Layout speculare a `CalendarTab` (store): heading + 4 stat tile + grid
 * 7×N + dialog di dettaglio al click sul giorno. Differenze rispetto allo
 * store:
 *   - sorgente dati: `GET /api/staff/{staff_id}/kpi-daily?from_=&to=` →
 *     righe `staff_daily_performance` (date, metrics, store_id, fresha_name).
 *   - nessun budget per dipendente per giorno → niente semaforo vs target,
 *     niente forecast, niente anomalie (per ora). Colore della cella basato
 *     sulla presenza/quantità di vendite.
 *   - KPI visualizzati: total_sales (hero), services_sold (sub), avg
 *     service value, tips, products_sold.
 *
 * Reuse: il file replica `buildCalendarGrid`/`MONTH_NAMES`/`WEEKDAYS`/`fmt`
 * inline (locali) per evitare cross-import su CalendarTab che è grosso e
 * specifico-store. Helper sono <50 righe e non meritano un modulo dedicato.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Check, X, Activity } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import api from '../lib/api';
import { fmt } from '../lib/calendarFormat';

const MONTH_NAMES = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];
const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

/** Mappatura KPI staff → label/format. Tieni sincronizzato con METRICS_MAP
 * in `backend/services/fresha_import.py` ed espandi se aggiungi colonne. */
const KPI_VIEW = [
  ['total_sales', 'Vendite totali', 'currency'],
  ['services_revenue', 'Ricavo servizi', 'currency'],
  ['products_revenue', 'Ricavo prodotti', 'currency'],
  ['services_sold', 'Servizi venduti', 'integer'],
  ['products_sold', 'Prodotti venduti', 'integer'],
  ['avg_service_value', 'Ticket medio', 'currency'],
  ['tips', 'Mance', 'currency'],
  ['rebook_pct', 'Rebook', 'percentage'],
];

/** Chiavi sempre visibili nel dettaglio giornaliero anche quando valgono 0
 * (richiesta business 2026-07-01: Servizi + Prodotti sempre presenti). */
const ALWAYS_SHOW_KEYS = new Set(['total_sales', 'services_sold', 'products_sold']);

function buildCalendarGrid(year, month /* 1..12 */) {
  const first = new Date(Date.UTC(year, month - 1, 1));
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const startWeekday = (first.getUTCDay() + 6) % 7; // shift so Monday=0
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= lastDay; d++) {
    cells.push(`${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

/** Colore cella in base alla quantità di vendite. Quartili semplici sui
 * dati visibili del mese: ≥75° percentile = verde, ≥50° = ambra, <50° =
 * neutro chiaro. Niente vendite = grigio. */
function bucketColors(rowsByDate) {
  const values = Object.values(rowsByDate)
    .map(r => r?.metrics?.total_sales)
    .filter(v => typeof v === 'number' && v > 0)
    .sort((a, b) => a - b);
  if (values.length === 0) return { p50: null, p75: null };
  const p = (q) => values[Math.max(0, Math.floor(values.length * q) - 1)];
  return { p50: p(0.5), p75: p(0.75) };
}

function StatTile({ label, value }) {
  return (
    <div className="border border-slate-200 bg-white p-3" data-testid={`stat-tile-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">{label}</p>
      <p className="font-heading text-lg font-bold mt-0.5 tabular-nums">{value}</p>
    </div>
  );
}

export default function StaffCalendarTab({ staffId, staffName }) {
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    setLoading(true);
    const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const from = `${year}-${String(month).padStart(2, '0')}-01`;
    const to = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    api.get(`/staff/${staffId}/kpi-daily`, { params: { from_: from, to } })
      .then(res => setRows(res.data.rows || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [staffId, month, year]);

  const grid = useMemo(() => buildCalendarGrid(year, month), [year, month]);

  const rowsByDate = useMemo(() => {
    const out = {};
    for (const r of rows) out[r.date] = r;
    return out;
  }, [rows]);

  const colors = useMemo(() => bucketColors(rowsByDate), [rowsByDate]);

  const stats = useMemo(() => {
    const dates = Object.keys(rowsByDate);
    let totalSales = 0;
    let totalServices = 0;
    let totalProducts = 0;
    let ticketSum = 0;
    let ticketN = 0;
    for (const d of dates) {
      const m = rowsByDate[d].metrics || {};
      if (typeof m.total_sales === 'number') totalSales += m.total_sales;
      if (typeof m.services_sold === 'number') totalServices += m.services_sold;
      if (typeof m.products_sold === 'number') totalProducts += m.products_sold;
      if (typeof m.avg_service_value === 'number' && m.avg_service_value > 0) {
        ticketSum += m.avg_service_value;
        ticketN += 1;
      }
    }
    return {
      daysWithData: dates.length,
      totalDaysInMonth: grid.filter(Boolean).length,
      totalSales,
      avgTicket: ticketN > 0 ? ticketSum / ticketN : 0,
      totalServices,
      totalProducts,
    };
  }, [rowsByDate, grid]);

  const goPrevMonth = () => {
    if (month === 1) { setMonth(12); setYear(year - 1); }
    else setMonth(month - 1);
  };
  const goNextMonth = () => {
    if (month === 12) { setMonth(1); setYear(year + 1); }
    else setMonth(month + 1);
  };

  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-5" data-testid="staff-calendar-tab">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-xl font-semibold tracking-tight">Giornaliero · {staffName}</h2>
          <p className="text-sm text-slate-500 mt-0.5">Dati Fresha per giornata · clicca un giorno per il dettaglio</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 border border-slate-200" data-testid="staff-calendar-month-switcher">
            <button onClick={goPrevMonth} className="p-2 hover:bg-slate-100" data-testid="staff-calendar-prev-month" aria-label="Mese precedente">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-4 text-sm font-semibold tabular-nums w-44 text-center">
              {MONTH_NAMES[month - 1]} {year}
            </span>
            <button onClick={goNextMonth} className="p-2 hover:bg-slate-100" data-testid="staff-calendar-next-month" aria-label="Mese successivo">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="staff-calendar-stats">
        <StatTile label="Giorni con dati" value={`${stats.daysWithData} / ${stats.totalDaysInMonth}`} />
        <StatTile label="Vendite del mese" value={fmt(stats.totalSales, 'currency')} />
        <StatTile label="Servizi venduti" value={fmt(stats.totalServices, 'integer')} />
        <StatTile label="Prodotti venduti" value={fmt(stats.totalProducts, 'integer')} />
        <StatTile label="Ticket medio" value={fmt(stats.avgTicket, 'currency')} />
      </div>

      {/* Hint quando vuoto */}
      {!loading && stats.daysWithData === 0 && (
        <div className="border border-dashed border-slate-200 p-6 text-center bg-slate-50" data-testid="staff-calendar-empty">
          <p className="text-sm text-slate-600 font-medium">Nessun dato per {MONTH_NAMES[month - 1]} {year}</p>
          <p className="text-xs text-slate-500 mt-1">
            I dati arrivano dall'estensione Chrome (auto-import alle 07:30) o da import manuali.
            Assicurati che il dipendente sia mappato a un alias Fresha.
          </p>
        </div>
      )}

      {/* Grid 7×N */}
      <div className="border border-slate-200 bg-white">
        {/* Weekday header */}
        <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
          {WEEKDAYS.map(w => (
            <div key={w} className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 text-center py-2">{w}</div>
          ))}
        </div>
        <div className="grid grid-cols-7" data-testid="staff-calendar-grid">
          {grid.map((iso, i) => (
            <DayCell
              key={iso || `pad-${i}`}
              iso={iso}
              row={iso ? rowsByDate[iso] : null}
              isFuture={iso && iso > todayIso}
              isToday={iso === todayIso}
              colors={colors}
              onClick={() => iso && setSelectedDate(iso)}
            />
          ))}
        </div>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selectedDate} onOpenChange={(o) => !o && setSelectedDate(null)}>
        <DialogContent className="max-w-md" data-testid="staff-calendar-day-dialog">
          <DialogHeader>
            <DialogTitle>
              {selectedDate ? new Date(selectedDate + 'T00:00:00').toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : ''}
            </DialogTitle>
            <DialogDescription>
              {selectedDate && rowsByDate[selectedDate]
                ? `Dati Fresha · ${rowsByDate[selectedDate].fresha_name || staffName}`
                : 'Nessun dato per questa giornata'}
            </DialogDescription>
          </DialogHeader>
          {selectedDate && rowsByDate[selectedDate] ? (
            <div className="space-y-1.5 max-h-96 overflow-y-auto" data-testid="staff-calendar-day-detail">
              {KPI_VIEW.map(([key, label, fmtType]) => {
                const v = rowsByDate[selectedDate]?.metrics?.[key];
                // Nascondi solo se completamente assente. Se 0, mostralo
                // per le chiavi in ALWAYS_SHOW_KEYS (business req).
                if (v == null) {
                  if (!ALWAYS_SHOW_KEYS.has(key)) return null;
                  // Fallthrough: render placeholder 0 sotto.
                }
                if (typeof v === 'number' && v === 0 && !ALWAYS_SHOW_KEYS.has(key)) return null;
                const display = v == null ? 0 : v;
                return (
                  <div key={key} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
                    <span className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{label}</span>
                    <span className="text-sm font-bold tabular-nums">{fmt(display, fmtType)}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-sm text-slate-500 py-4 text-center">
              Nessun import per questa data. I dati Fresha arrivano dopo le 07:30 del mattino seguente.
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DayCell({ iso, row, isFuture, isToday, colors, onClick }) {
  if (!iso) return <div className="h-14 md:h-20" aria-hidden="true" />;
  const day = parseInt(iso.slice(8, 10), 10);
  const sales = row?.metrics?.total_sales;
  const services = row?.metrics?.services_sold;
  const hasData = sales != null && sales > 0;

  let bg = 'bg-white border-slate-100 text-slate-300';
  if (!isFuture) {
    if (hasData) {
      if (colors.p75 && sales >= colors.p75) bg = 'bg-emerald-100 hover:bg-emerald-200 border-emerald-300 text-slate-900';
      else if (colors.p50 && sales >= colors.p50) bg = 'bg-amber-50 hover:bg-amber-100 border-amber-200 text-slate-900';
      else bg = 'bg-slate-100 hover:bg-slate-200 border-slate-200 text-slate-700';
    } else {
      bg = 'bg-slate-50 hover:bg-slate-100 border-slate-200 text-slate-500';
    }
  }
  const ring = isToday ? 'ring-2 ring-slate-900 ring-offset-1' : '';

  return (
    <button
      onClick={onClick}
      disabled={isFuture}
      className={`relative h-14 md:h-20 border ${bg} ${ring} text-left p-1 md:p-1.5 transition-all disabled:cursor-not-allowed`}
      data-testid={`staff-calendar-day-${iso}`}
    >
      <div className="flex items-start justify-between gap-0.5">
        <span className="text-[11px] md:text-xs font-semibold tabular-nums">{day}</span>
        {!isFuture && (
          hasData ? (
            <span className="inline-flex items-center justify-center w-3 h-3 md:w-3.5 md:h-3.5 bg-emerald-600 text-white" title="Dato importato">
              <Check className="w-2 h-2 md:w-2.5 md:h-2.5" strokeWidth={3} />
            </span>
          ) : (
            <span className="inline-flex items-center justify-center w-3 h-3 md:w-3.5 md:h-3.5 bg-slate-200 text-slate-500" title="Nessun dato">
              <X className="w-2 h-2 md:w-2.5 md:h-2.5" strokeWidth={2.5} />
            </span>
          )
        )}
      </div>
      {hasData && (
        <div className="mt-0.5 leading-tight" data-testid={`staff-calendar-day-${iso}-value`}>
          <p className="text-[10px] md:text-xs font-bold tabular-nums truncate">
            {fmt(sales, 'currency')}
          </p>
          {services != null && services > 0 && (
            <p className="text-[9px] md:text-[10px] text-slate-600 truncate">
              {services} {services === 1 ? 'servizio' : 'servizi'}
            </p>
          )}
        </div>
      )}
    </button>
  );
}
